import { useMemo, useCallback } from 'react';
import { DefaultRootState, useSelector, useDispatch } from 'react-redux';
import { v4 } from 'uuid';

import { PriceType } from 'common/api/autogenerated/trading-gateway';

import Logger from 'common/services/Logger/Logger';
import Storage from 'common/services/Storage/Storage';

import { syncPricesChanges } from 'components/AssetsList/redux/assets.mapper';

import { actionInvokeSignalR } from 'blocks/SocketProvider/redux/socket.actions';

import config from './config';

const storage = new Storage('spot');
const log = new Logger('Spot');

export const useStream = (initialSymbol?: string | null) => {
  const asset = useSelector((state: DefaultRootState) => state.assetsReducer.assets[initialSymbol || '']);
  const coin = useSelector((state: DefaultRootState) => state.assetsReducer.coins[initialSymbol || '']);
  const info = useSelector((state: DefaultRootState) => state.assetsReducer.info);
  const priceType = useSelector((state: DefaultRootState) => state.common.user.priceType);
  const stream = useSelector((state: DefaultRootState) => state.socketReducer.stream[initialSymbol || '']);

  const dispatch = useDispatch();

  const unsubscribe = useCallback(
    (symbol: string, type = priceType, guid = '') => {
      const subscribeSymbol = `${symbol}:${type}`;

      if (storage.isLast(subscribeSymbol) && type) {
        log.warn('[unsubscribe] =>', subscribeSymbol);
        dispatch(
          actionInvokeSignalR({
            ...config.unsubscribeConfig(symbol, type),
            ref: guid,
          }),
        );
      }
      storage.remove(subscribeSymbol);
    },
    [dispatch, priceType],
  );

  const subscribe = useCallback(
    (symbol: string, type = priceType) => {
      if (!type) return { unsubscribe: () => unsubscribe(symbol, type) };

      const subscribeGuid = v4();
      const subscribeSymbol = `${symbol}:${type}`;

      if (!storage.isExist(subscribeSymbol)) {
        log.success('[subscribe] =>', subscribeSymbol);

        dispatch(
          actionInvokeSignalR({
            ...config.subscribeConfig(symbol, type),
            id: subscribeGuid,
          }),
        );
      }
      storage.add(subscribeSymbol);

      return {
        unsubscribe: () => {
          unsubscribe(symbol, type, subscribeGuid);
        },
      };
    },
    [priceType, dispatch, unsubscribe],
  );

  const bid = useMemo(() => Number(stream?.bid || asset?.bid || 0), [asset?.bid, stream?.bid]);

  const prevBid = useMemo(() => Number(stream?.prevBid || asset?.prevBid), [asset?.prevBid, stream?.prevBid]);

  const ask = useMemo(() => Number(stream?.ask || asset?.ask || 0), [asset?.ask, stream?.ask]);

  const prevAsk = useMemo(() => Number(stream?.prevAsk || asset?.prevAsk), [asset?.prevAsk, stream?.prevAsk]);

  const last = useMemo(() => Number(stream?.price || asset?.price), [asset?.price, stream?.price]);

  const prevLast = useMemo(() => Number(stream?.prevPrice || asset?.prevPrice), [asset?.prevPrice, stream?.prevPrice]);

  const price = useMemo(() => {
    switch (priceType) {
      case PriceType.BEST_ASK:
        return ask;
      case PriceType.BEST_BID:
        return bid;
      case PriceType.LAST_PRICE:
        return last;

      default:
        return last;
    }
  }, [ask, bid, last, priceType]);

  const prevPrice = useMemo(() => {
    switch (priceType) {
      case PriceType.BEST_ASK:
        return prevAsk;
      case PriceType.BEST_BID:
        return prevBid;
      case PriceType.LAST_PRICE:
        return prevLast;

      default:
        return prevLast;
    }
  }, [priceType, prevAsk, prevBid, prevLast]);

  const pricesChanges = useMemo(() => syncPricesChanges(price, info.pricesChanges), [info.pricesChanges, price]);

  const result = useMemo(
    () => ({
      subscribe,
      unsubscribe,
      stream: {
        ...info,
        ...asset,
        ...coin,
        price,
        prevPrice,
        pricesChanges,
        bid,
        ask,
        prevBid,
        prevAsk,
      },
      priceType,
      info,
      price,
      bid,
      ask,
      prevBid,
      prevAsk,
    }),
    [ask, asset, bid, coin, info, prevAsk, prevBid, prevPrice, price, priceType, pricesChanges, subscribe, unsubscribe],
  );

  return result;
};
